import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  FileCheck2,
  FilePlus2,
  FolderOpen,
  Gauge,
  Link2,
  LockKeyhole,
  RotateCcw,
  Trash2,
  Undo2,
} from 'lucide-react';

import {
  interpolateAmbientOutputFactor,
  projectGenerationProjectDutyExhibitBasis,
  type GenerationProjectDutyBasisDraft,
} from '../../../lib/generationImpact/generationProjectDuty';
import {
  resolveProjectDutyDocumentPrefill,
  type ProjectDutyDocumentPrefill,
} from '../../../lib/generationImpact/projectDutyDocumentPrefill';
import {
  listGenerationDocumentEvidenceCandidates,
  type GenerationDocumentEvidenceCandidate,
} from '../../../lib/generationImpact/generationDocumentEvidence';
import type { SiteEntity } from '../../../types/site';
import type { SiteDocument } from '../../../types/siteRag';
import type {
  GenerationImpactScenarioInput,
  GenerationProjectDutyAuthorityRole,
  GenerationProjectFuelGasSupplyDuty,
  GenerationProjectDutyCompleteness,
  GenerationProjectDutyEvidenceStatus,
  GenerationProjectDutySource,
  GenerationProjectReservationEnvelope,
} from '../../../types/generationImpact';

interface GenerationProjectDutyBoardProps {
  input: GenerationImpactScenarioInput;
  designDryBulbC: number;
  /**
   * Where `designDryBulbC` came from. A monthly-maximum fallback is a different
   * measurement from a recorded site peak, so the form says which one it is
   * rather than presenting both as "the" design dry bulb.
   */
  designDryBulbBasis?: 'sed-peak' | 'monthly-maximum' | 'missing';
  documents?: readonly SiteDocument[];
  /** Seed empty dossier fields from validated document facts; never applies authority. */
  documentPrefillEnabled?: boolean;
  evidenceMode?: SiteEntity['evidenceMode'];
  readOnly?: boolean;
  actionsDisabled?: boolean;
  appliedStatus?: string | null;
  onApply: (draft: GenerationProjectDutyBasisDraft) => void;
  onClear: () => void;
  onOpenDocuments?: (documentId?: string) => void;
  /** Reports whether the local dossier form differs from the last applied/bound baseline. */
  onDraftDirtyChange?: (dirty: boolean) => void;
  /**
   * Identity of the scenario currently loaded into the editor. Changing it resets
   * the local draft even when the bound equipment and applied dossier are identical,
   * so unapplied edits never leak from one scenario into another.
   */
  scenarioResetKey?: string | null;
  /**
   * Rendered inside a workbench row that already carries the title, status
   * and bound-catalog context — suppress the board's own header so the
   * dossier is titled once.
   */
  embedded?: boolean;
}

type SourceForm = {
  id: string;
  disclosure: 'public' | 'private';
  authorityRole: GenerationProjectDutyAuthorityRole | '';
  publisher: string;
  title: string;
  revision: string;
  issuedAt: string;
  url: string;
  retrievedAt: string;
  fileName: string;
  sha256: string;
  siteDocumentId: string;
};

type EnvelopeKey =
  | 'generationBlockEnvelope'
  | 'coolingEnvelope'
  | 'switchyardEnvelope'
  | 'ccsCaptureEnvelope'
  | 'ccsCompressionEnvelope'
  | 'naturalGasRouteEnvelope'
  | 'co2ExportRouteEnvelope'
  | 'constructionLaydownEnvelope';

type EnvelopeForm = Record<EnvelopeKey, { lengthM: string; widthM: string }>;

type WaterMode = 'none' | 'gross-output-intensity' | 'absolute-design-day';
type AcousticMode = 'none' | 'sound-power' | 'reference-spl';

interface DutyForm {
  basisId: string;
  basisName: string;
  sources: SourceForm[];
  referenceIsoGrossMWPerBlock: string;
  ambientCurve: string;
  degradationPct: string;
  auxiliaryLoadMWPerBlock: string;
  heatRateBtuPerKWhHhv: string;
  forcedOutagePct: string;
  elevationM: string;
  relativeHumidityPct: string;
  barometricPressureKPaAbsolute: string;
  referenceConditionNotes: string;
  normalizationNote: string;
  performanceSourceIds: string;
  envelopes: EnvelopeForm;
  installedSourceIds: string;
  maintenanceClearanceIncluded: boolean;
  fireAccessIncluded: boolean;
  gasMinimumRequiredKPa: string;
  gasMaximumAllowedKPa: string;
  gasSiteSupplyMinimumKPa: string;
  gasMinimumRequiredReference: 'gauge' | 'absolute';
  gasMaximumAllowedReference: 'gauge' | 'absolute';
  gasSiteSupplyMinimumReference: 'gauge' | 'absolute';
  gasSiteSupplyMaximumKPa: string;
  gasSiteSupplyMaximumReference: 'gauge' | 'absolute';
  gasSourceIds: string;
  gasSupplyQuantityBasis: '' | 'firm-transport-agreement' | 'indicative';
  gasSupplyCapacityKind: 'energy' | 'standard-volume';
  gasSupplyStationCapacity: string;
  gasSupplyContractMaxDailyQuantity: string;
  gasSupplyHhvBtuPerScf: string;
  gasSupplyBasePressureKPaAbsolute: string;
  gasSupplyBaseTemperatureC: string;
  gasSupplyRatedAtPressureKPa: string;
  gasSupplyRatedAtPressureReference: 'gauge' | 'absolute';
  gasSupplyZAtRated: string;
  gasSupplyZAtMinimum: string;
  gasSupplyOperatingCase: string;
  waterMode: WaterMode;
  waterWithdrawal: string;
  waterConsumption: string;
  waterReferenceGrossMWhPerDay: string;
  waterOperatingCase: string;
  waterSourceIds: string;
  acousticMode: AcousticMode;
  acousticLabel: string;
  acousticQuantity: string;
  acousticQuantityScope: 'site-total' | 'per-generation-block';
  acousticOperatingCase: string;
  acousticLevelDbA: string;
  acousticDirectivityCorrectionDb: string;
  acousticReferenceDistanceM: string;
  acousticReferenceScope: 'aggregate' | 'per-unit';
  acousticBasisLabel: string;
  acousticSourceIds: string;
}

const authorityRoles: Array<{ value: GenerationProjectDutyAuthorityRole; label: string }> = [
  { value: 'oem', label: 'OEM' },
  { value: 'epc', label: 'EPC' },
  { value: 'owner-engineer', label: "Owner's engineer" },
  { value: 'utility', label: 'Gas / electric utility' },
  { value: 'acoustic-consultant', label: 'Acoustic consultant' },
  { value: 'water-provider', label: 'Water provider' },
  { value: 'permit-study', label: 'Permit study' },
];

const envelopeLabels: Record<EnvelopeKey, string> = {
  generationBlockEnvelope: 'Installed generation block',
  coolingEnvelope: 'Cooling system',
  switchyardEnvelope: 'Switchyard',
  ccsCaptureEnvelope: 'CCS capture island',
  ccsCompressionEnvelope: 'CCS compression',
  naturalGasRouteEnvelope: 'Natural-gas route',
  co2ExportRouteEnvelope: 'CO₂ export route',
  constructionLaydownEnvelope: 'Construction laydown',
};

/** Every envelope except the one the validator actually requires. */
const OPTIONAL_ENVELOPE_KEYS = (Object.keys(envelopeLabels) as EnvelopeKey[])
  .filter(key => key !== 'generationBlockEnvelope');

const evidenceLabels: Array<{
  key: keyof GenerationProjectDutyCompleteness['statuses'];
  short: string;
  full: string;
}> = [
  { key: 'ambientPerformance', short: 'PERF', full: 'Ambient performance' },
  { key: 'fuelGasPressure', short: 'GAS', full: 'Fuel-gas pressure' },
  { key: 'acousticSource', short: 'SND', full: 'Acoustic source' },
  { key: 'waterBalance', short: 'H₂O', full: 'Water balance' },
  { key: 'installedPlant', short: 'PLNT', full: 'Installed plant' },
  { key: 'naturalGasRoute', short: 'G-RTE', full: 'Natural-gas route' },
  { key: 'ccsIntegration', short: 'CCS', full: 'CCS integration' },
  { key: 'reliability', short: 'REL', full: 'Reliability' },
];

const emptyEnvelopes = (): EnvelopeForm => ({
  generationBlockEnvelope: { lengthM: '', widthM: '' },
  coolingEnvelope: { lengthM: '', widthM: '' },
  switchyardEnvelope: { lengthM: '', widthM: '' },
  ccsCaptureEnvelope: { lengthM: '', widthM: '' },
  ccsCompressionEnvelope: { lengthM: '', widthM: '' },
  naturalGasRouteEnvelope: { lengthM: '', widthM: '' },
  co2ExportRouteEnvelope: { lengthM: '', widthM: '' },
  constructionLaydownEnvelope: { lengthM: '', widthM: '' },
});

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptySource(index = 1): SourceForm {
  return {
    id: `project-source-${index}`,
    disclosure: 'private',
    authorityRole: 'epc',
    publisher: '',
    title: '',
    revision: '',
    issuedAt: '',
    url: '',
    retrievedAt: today(),
    fileName: '',
    sha256: '',
    siteDocumentId: '',
  };
}

function finiteString(value: number | undefined): string {
  return value == null ? '' : String(value);
}

function sourceForm(source: GenerationProjectDutySource): SourceForm {
  return {
    id: source.id,
    disclosure: source.disclosure,
    authorityRole: source.authorityRole,
    publisher: source.publisher,
    title: source.title,
    revision: source.revision ?? '',
    issuedAt: source.issuedAt ?? '',
    url: source.disclosure === 'public' ? source.url : '',
    retrievedAt: source.disclosure === 'public' ? source.retrievedAt : today(),
    fileName: source.disclosure === 'private' ? source.fileName : '',
    sha256: source.disclosure === 'private' ? source.sha256 : '',
    siteDocumentId: source.disclosure === 'private' ? source.siteDocumentId ?? '' : '',
  };
}

function envelopeForm(
  envelope: GenerationProjectReservationEnvelope | undefined,
): { lengthM: string; widthM: string } {
  return {
    lengthM: finiteString(envelope?.lengthM),
    widthM: finiteString(envelope?.widthM),
  };
}

function curveText(
  points: readonly { dryBulbC: number; grossOutputFactor: number }[] | undefined,
): string {
  return points?.map(point => `${point.dryBulbC}, ${point.grossOutputFactor}`).join('\n') ?? '';
}

function formFromInput(input: GenerationImpactScenarioInput): DutyForm {
  const basis = input.projectDutyBasis;
  const installed = basis?.installedReservations;
  const performance = basis?.performance;
  const gas = basis?.fuelGas;
  const water = basis?.water;
  const acoustic = basis?.acoustics?.acousticSources[0];
  const datum = acoustic?.appliedDatum;
  const defaultSourceRefs = basis?.sources[0]?.id ?? 'project-source-1';
  return {
    basisId: basis?.basisId ?? `${input.siteId}-project-duty`,
    basisName: basis?.basisName ?? `${input.name} · project OEM/EPC duty`,
    sources: basis?.sources.map(sourceForm) ?? [emptySource()],
    referenceIsoGrossMWPerBlock: finiteString(performance?.referenceIsoGrossMWPerBlock),
    ambientCurve: curveText(performance?.ambientCorrection?.points),
    degradationPct: performance?.degradationFactor == null
      ? ''
      : String(performance.degradationFactor * 100),
    auxiliaryLoadMWPerBlock: finiteString(performance?.auxiliaryLoadMWPerBlock),
    heatRateBtuPerKWhHhv: finiteString(performance?.heatRateBtuPerKWhHhv),
    forcedOutagePct: performance?.forcedOutageRate == null
      ? ''
      : String(performance.forcedOutageRate * 100),
    elevationM: finiteString(performance?.referenceConditions.elevationM),
    relativeHumidityPct: finiteString(performance?.referenceConditions.relativeHumidityPct),
    barometricPressureKPaAbsolute: finiteString(
      performance?.referenceConditions.barometricPressureKPaAbsolute,
    ),
    referenceConditionNotes: performance?.referenceConditions.notes ?? '',
    normalizationNote: performance?.normalizationNote ?? '',
    performanceSourceIds: performance?.sourceIds.join(', ') ?? defaultSourceRefs,
    envelopes: {
      generationBlockEnvelope: envelopeForm(installed?.generationBlockEnvelope),
      coolingEnvelope: envelopeForm(installed?.coolingEnvelope),
      switchyardEnvelope: envelopeForm(installed?.switchyardEnvelope),
      ccsCaptureEnvelope: envelopeForm(installed?.ccsCaptureEnvelope),
      ccsCompressionEnvelope: envelopeForm(installed?.ccsCompressionEnvelope),
      naturalGasRouteEnvelope: envelopeForm(installed?.naturalGasRouteEnvelope),
      co2ExportRouteEnvelope: envelopeForm(installed?.co2ExportRouteEnvelope),
      constructionLaydownEnvelope: envelopeForm(installed?.constructionLaydownEnvelope),
    },
    maintenanceClearanceIncluded:
      installed?.generationBlockEnvelope.maintenanceClearanceIncluded ?? false,
    fireAccessIncluded: installed?.generationBlockEnvelope.fireAccessIncluded ?? false,
    installedSourceIds: installed?.sourceIds.join(', ') ?? defaultSourceRefs,
    gasMinimumRequiredKPa: finiteString(gas?.minimumRequired.valueKPa),
    gasMaximumAllowedKPa: finiteString(gas?.maximumAllowed?.valueKPa),
    gasSiteSupplyMinimumKPa: finiteString(gas?.documentedSiteSupplyMinimum?.valueKPa),
    gasMinimumRequiredReference: gas?.minimumRequired.reference ?? 'gauge',
    gasMaximumAllowedReference: gas?.maximumAllowed?.reference
      ?? gas?.minimumRequired.reference
      ?? 'gauge',
    gasSiteSupplyMinimumReference: gas?.documentedSiteSupplyMinimum?.reference
      ?? gas?.minimumRequired.reference
      ?? 'gauge',
    gasSiteSupplyMaximumKPa: finiteString(gas?.documentedSiteSupplyMaximum?.valueKPa),
    gasSiteSupplyMaximumReference: gas?.documentedSiteSupplyMaximum?.reference
      ?? gas?.minimumRequired.reference
      ?? 'gauge',
    gasSourceIds: gas?.sourceIds.join(', ') ?? defaultSourceRefs,
    gasSupplyQuantityBasis: gas?.supply?.quantityBasis ?? '',
    gasSupplyCapacityKind: gas?.supply?.capacityBasis.kind ?? 'standard-volume',
    gasSupplyStationCapacity: finiteString(gas?.supply?.stationDesignCapacity),
    gasSupplyContractMaxDailyQuantity: finiteString(gas?.supply?.contractMaxDailyQuantity),
    gasSupplyHhvBtuPerScf: finiteString(
      gas?.supply?.capacityBasis.kind === 'standard-volume'
        ? gas.supply.capacityBasis.documentedHhvBtuPerScf
        : undefined,
    ),
    gasSupplyBasePressureKPaAbsolute: finiteString(
      gas?.supply?.capacityBasis.kind === 'standard-volume'
        ? gas.supply.capacityBasis.standardConditions.basePressureKPaAbsolute
        : undefined,
    ),
    gasSupplyBaseTemperatureC: finiteString(
      gas?.supply?.capacityBasis.kind === 'standard-volume'
        ? gas.supply.capacityBasis.standardConditions.baseTemperatureC
        : undefined,
    ),
    gasSupplyRatedAtPressureKPa: finiteString(gas?.supply?.ratedAtPressure.valueKPa),
    gasSupplyRatedAtPressureReference: gas?.supply?.ratedAtPressure.reference
      ?? gas?.minimumRequired.reference
      ?? 'gauge',
    gasSupplyZAtRated: finiteString(gas?.supply?.compressibility?.atRatedPressure),
    gasSupplyZAtMinimum: finiteString(gas?.supply?.compressibility?.atMinimumDeliveryPressure),
    gasSupplyOperatingCase: gas?.supply?.operatingCase ?? '',
    waterMode: water?.kind ?? 'none',
    waterWithdrawal: water?.kind === 'gross-output-intensity'
      ? String(water.withdrawalGalPerMWhGross)
      : water?.kind === 'absolute-design-day' ? String(water.withdrawalGalPerDay) : '',
    waterConsumption: water?.kind === 'gross-output-intensity'
      ? String(water.consumptionGalPerMWhGross)
      : water?.kind === 'absolute-design-day' ? String(water.consumptionGalPerDay) : '',
    waterReferenceGrossMWhPerDay: water?.kind === 'absolute-design-day'
      ? String(water.referenceGrossMWhPerDay)
      : '',
    waterOperatingCase: water?.operatingCase ?? '',
    waterSourceIds: water?.sourceIds.join(', ') ?? defaultSourceRefs,
    acousticMode: datum?.kind ?? 'none',
    acousticLabel: acoustic?.label ?? '',
    acousticQuantity: finiteString(acoustic?.quantity.count),
    acousticQuantityScope: acoustic?.quantity.scope ?? 'per-generation-block',
    acousticOperatingCase: acoustic?.operatingCase ?? '',
    acousticLevelDbA: finiteString(datum?.levelDbA),
    acousticDirectivityCorrectionDb: datum?.kind === 'sound-power'
      ? finiteString(datum.directivityCorrectionDb)
      : '',
    acousticReferenceDistanceM: datum?.kind === 'reference-spl'
      ? String(datum.referenceDistanceM)
      : '',
    acousticReferenceScope: datum?.kind === 'reference-spl' ? datum.scope : 'aggregate',
    acousticBasisLabel: datum?.kind === 'reference-spl' ? datum.basisLabel ?? '' : '',
    acousticSourceIds: acoustic?.sourceIds.join(', ') ?? defaultSourceRefs,
  };
}

function sameDutyForm(left: DutyForm, right: DutyForm): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function mergeSourceIds(...groups: Array<readonly string[] | undefined>): string {
  return [...new Set(groups.flatMap(group => group ?? []))].sort().join(', ');
}

function mergeDocumentPrefill(
  current: DutyForm,
  prefill: ProjectDutyDocumentPrefill,
): DutyForm {
  if (prefill.sources.length === 0) return current;
  const linkedSources: SourceForm[] = prefill.sources.map(source => ({
    id: source.id,
    disclosure: 'private',
    // Deliberately incomplete: the operator must identify and verify the
    // issuing authority before the existing Apply workflow accepts it.
    authorityRole: '',
    publisher: '',
    title: source.fileName,
    revision: '',
    issuedAt: '',
    url: '',
    retrievedAt: today(),
    fileName: source.fileName,
    sha256: source.sha256,
    siteDocumentId: source.documentId,
  }));
  const existingIds = new Set(current.sources.map(source => source.id));
  const additions = linkedSources.filter(source => !existingIds.has(source.id));
  let sources = current.sources;
  if (additions.length > 0) {
    const pristineIndex = current.sources.findIndex(isPristineSource);
    if (pristineIndex >= 0) {
      sources = current.sources.map((source, index) => (
        index === pristineIndex ? additions[0]! : source
      ));
      sources = [...sources, ...additions.slice(1)];
    } else {
      sources = [...current.sources, ...additions];
    }
  }

  let performanceSourceIds = current.performanceSourceIds;
  const performanceIds: string[][] = [];
  const next: DutyForm = {
    ...current,
    sources,
    envelopes: { ...current.envelopes },
  };
  if (!current.referenceIsoGrossMWPerBlock
    && prefill.values.referenceIsoGrossMWPerBlock != null) {
    next.referenceIsoGrossMWPerBlock = String(prefill.values.referenceIsoGrossMWPerBlock);
    performanceIds.push(prefill.fieldSourceIds.referenceIsoGrossMWPerBlock ?? []);
  }
  if (!current.ambientCurve && prefill.values.ambientCurve?.length) {
    next.ambientCurve = prefill.values.ambientCurve
      .map(point => `${point.dryBulbC}, ${point.grossOutputFactor}`)
      .join('\n');
    performanceIds.push(prefill.fieldSourceIds.ambientCurve ?? []);
  }
  if (!current.auxiliaryLoadMWPerBlock
    && prefill.values.auxiliaryLoadMWPerBlock != null) {
    next.auxiliaryLoadMWPerBlock = String(prefill.values.auxiliaryLoadMWPerBlock);
    performanceIds.push(prefill.fieldSourceIds.auxiliaryLoadMWPerBlock ?? []);
  }
  if (!current.heatRateBtuPerKWhHhv
    && prefill.values.heatRateBtuPerKWhHhv != null) {
    next.heatRateBtuPerKWhHhv = String(prefill.values.heatRateBtuPerKWhHhv);
    performanceIds.push(prefill.fieldSourceIds.heatRateBtuPerKWhHhv ?? []);
  }
  const mergedPerformanceIds = mergeSourceIds(...performanceIds);
  if (mergedPerformanceIds) performanceSourceIds = mergedPerformanceIds;
  next.performanceSourceIds = performanceSourceIds;

  const installedIds: string[][] = [];
  for (const [key, envelope] of Object.entries(prefill.values.envelopes ?? {}) as Array<
    [EnvelopeKey, { lengthM: number; widthM: number }]
  >) {
    const existing = current.envelopes[key];
    if (existing.lengthM || existing.widthM) continue;
    next.envelopes[key] = {
      lengthM: String(envelope.lengthM),
      widthM: String(envelope.widthM),
    };
    installedIds.push(
      prefill.fieldSourceIds[`envelopes.${key}.lengthM`] ?? [],
      prefill.fieldSourceIds[`envelopes.${key}.widthM`] ?? [],
    );
  }
  const mergedInstalledIds = mergeSourceIds(...installedIds);
  if (mergedInstalledIds) next.installedSourceIds = mergedInstalledIds;

  if (current.waterMode === 'none'
    && !current.waterWithdrawal
    && !current.waterConsumption
    && prefill.values.water) {
    next.waterMode = 'gross-output-intensity';
    next.waterWithdrawal = String(prefill.values.water.withdrawalGalPerMWhGross);
    next.waterConsumption = String(prefill.values.water.consumptionGalPerMWhGross);
    next.waterSourceIds = mergeSourceIds(
      prefill.fieldSourceIds.waterMode,
      prefill.fieldSourceIds.waterWithdrawal,
      prefill.fieldSourceIds.waterConsumption,
    );
  }

  if (current.acousticMode === 'none' && prefill.values.acoustic) {
    next.acousticMode = prefill.values.acoustic.mode;
    next.acousticLevelDbA = String(prefill.values.acoustic.levelDbA);
    if (prefill.values.acoustic.referenceDistanceM != null) {
      next.acousticReferenceDistanceM =
        String(prefill.values.acoustic.referenceDistanceM);
    }
    next.acousticSourceIds = mergeSourceIds(
      prefill.fieldSourceIds.acousticMode,
      prefill.fieldSourceIds.acousticLevelDbA,
      prefill.fieldSourceIds.acousticReferenceDistanceM,
    );
  }
  return next;
}

function equipmentBindingRevision(input: GenerationImpactScenarioInput): string {
  const equipment = input.equipmentBasis;
  if (equipment) return `${equipment.catalogId}:${equipment.appliedDutyFingerprint}`;
  // A user-supplied entry is an equally valid binding target; keying only off
  // the catalog basis would leave the draft un-reset when the applied user
  // entry changed underneath it.
  const entryId = input.equipmentWorkspace?.appliedUserEquipmentId;
  return entryId ? `user-equipment:${entryId}` : 'no-equipment-binding';
}

/** Any applied equipment authority a dossier may bind to. */
function boundEquipment(input: GenerationImpactScenarioInput): { label: string; unverified: boolean } | null {
  const equipment = input.equipmentBasis;
  if (equipment) return { label: `${equipment.manufacturer} · ${equipment.model}`, unverified: false };
  const entryId = input.equipmentWorkspace?.appliedUserEquipmentId;
  const entry = entryId
    ? input.equipmentWorkspace?.userEquipment.find(candidate => candidate.id === entryId)
    : undefined;
  return entry ? { label: `${entry.manufacturer} · ${entry.model}`, unverified: true } : null;
}

function appliedProjectDutyRevision(input: GenerationImpactScenarioInput): string {
  const basis = input.projectDutyBasis;
  return basis
    ? `${basis.basisId}:${basis.basisHash}:${basis.appliedDutyFingerprint}`
    : 'no-applied-project-duty';
}

function numberOrUndefined(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function requiredNumber(value: string, label: string, minimum = 0): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= minimum) {
    throw new RangeError(`${label} must be greater than ${minimum}.`);
  }
  return parsed;
}

/**
 * The validator's floor for an installed dimension. Stated here so the form
 * rejects 0.005 m with a readable message instead of letting it through to a
 * raw `RangeError` from `validateGenerationProjectDutyBasis`.
 */
const MIN_ENVELOPE_DIMENSION_M = 0.01;

function envelopeDimension(value: string, label: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < MIN_ENVELOPE_DIMENSION_M) {
    throw new RangeError(`${label} must be at least ${MIN_ENVELOPE_DIMENSION_M} m.`);
  }
  return parsed;
}

function optionalEnvelope(
  value: { lengthM: string; widthM: string },
  label: string,
): GenerationProjectReservationEnvelope | undefined {
  if (!value.lengthM.trim() && !value.widthM.trim()) return undefined;
  return {
    lengthM: envelopeDimension(value.lengthM, `${label} length`),
    widthM: envelopeDimension(value.widthM, `${label} width`),
  };
}

function parseAmbientCurve(value: string): Array<{
  dryBulbC: number;
  grossOutputFactor: number;
}> | undefined {
  if (!value.trim()) return undefined;
  const rows = value.trim().split(/\r?\n/).map((row, index) => {
    const columns = row.split(',').map(column => column.trim());
    if (columns.length !== 2) {
      throw new RangeError(`Ambient curve row ${index + 1} must be “dry bulb °C, gross factor”.`);
    }
    const dryBulbC = Number(columns[0]);
    const grossOutputFactor = Number(columns[1]);
    if (!Number.isFinite(dryBulbC) || !Number.isFinite(grossOutputFactor)) {
      throw new RangeError(`Ambient curve row ${index + 1} must contain two numbers.`);
    }
    return { dryBulbC, grossOutputFactor };
  });
  return rows;
}

function sourceFromForm(value: SourceForm): GenerationProjectDutySource {
  if (!value.id.trim() || !value.authorityRole || !value.publisher.trim() || !value.title.trim()) {
    throw new RangeError('Each evidence source needs an ID, authority, publisher, and title.');
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value.id.trim())) {
    throw new RangeError('Evidence source IDs may use only letters, numbers, hyphens, and underscores.');
  }
  const common = {
    id: value.id.trim(),
    authorityRole: value.authorityRole,
    publisher: value.publisher.trim(),
    title: value.title.trim(),
    ...(value.revision.trim() ? { revision: value.revision.trim() } : {}),
    ...(value.issuedAt ? { issuedAt: value.issuedAt } : {}),
  };
  if (value.disclosure === 'public') {
    if (!/^https:\/\//i.test(value.url.trim()) || !value.retrievedAt) {
      throw new RangeError('Public evidence needs an HTTPS URL and retrieval date.');
    }
    return {
      ...common,
      disclosure: 'public',
      url: value.url.trim(),
      retrievedAt: value.retrievedAt,
    };
  }
  if (!value.fileName.trim()
    || value.fileName.includes('/')
    || value.fileName.includes('\\')
    || !/^[a-f\d]{64}$/i.test(value.sha256.trim())) {
    throw new RangeError('Private evidence needs an inert file name and full SHA-256 digest.');
  }
  return {
    ...common,
    disclosure: 'private',
    fileName: value.fileName.trim(),
    sha256: value.sha256.trim().toLowerCase(),
    ...(value.siteDocumentId.trim() ? { siteDocumentId: value.siteDocumentId.trim() } : {}),
  };
}

function isPristineSource(value: SourceForm): boolean {
  return /^project-source-\d+$/.test(value.id)
    && value.authorityRole === 'epc'
    && !value.publisher
    && !value.title
    && !value.revision
    && !value.issuedAt
    && !value.url
    && !value.fileName
    && !value.sha256
    && !value.siteDocumentId;
}

function replaceSourceRef(value: string, previousId: string, nextId: string): string {
  return value
    .split(',')
    .map(sourceId => sourceId.trim() === previousId ? nextId : sourceId.trim())
    .filter(Boolean)
    .join(', ');
}

function parseSourceRefs(
  value: string,
  availableIds: ReadonlySet<string>,
  label: string,
): string[] {
  const ids = [...new Set(value.split(',').map(id => id.trim()).filter(Boolean))].sort();
  if (ids.length === 0) {
    throw new RangeError(`${label} needs at least one evidence source ID.`);
  }
  const unknown = ids.filter(id => !availableIds.has(id));
  if (unknown.length > 0) {
    throw new RangeError(`${label} references unknown source ID(s): ${unknown.join(', ')}.`);
  }
  return ids;
}

function statusTone(status: GenerationProjectDutyEvidenceStatus): string {
  if (status === 'supplied') return 'border-success bg-success/10 text-success';
  if (status === 'partial') return 'border-warning bg-warning/10 text-warning';
  if (status === 'not-applicable') return 'border-border bg-background text-text-secondary';
  return 'border-border bg-background text-text-muted';
}

/**
 * PROGRESSIVE DISCLOSURE — an optional dossier group, collapsed by default.
 *
 * The dossier used to render ~58 controls at once, of which SIX are actually
 * required to apply. Everything a project may or may not document — ambient
 * performance, fuel-gas pressures, the water balance, acoustics, and the seven
 * optional installed envelopes — now opens on demand, with its own supplied /
 * partial / missing state on the summary so nothing is hidden ambiguously.
 *
 * `<details>` keeps its children mounted, so the fieldset `disabled` state, the
 * draft-dirty comparison and every existing query still behave identically.
 * A group that already carries data opens itself, so an applied dossier still
 * shows what it documented.
 */
function OptionalGroup({
  title,
  hint,
  status,
  testId,
  children,
}: {
  title: string;
  hint?: string;
  status: GenerationProjectDutyEvidenceStatus;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <details
      className="border border-border rounded-md"
      data-status={status}
      data-testid={testId}
      open={status === 'supplied' || status === 'partial'}
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
        <span className="min-w-0">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-text-primary">{title}</span>
          {hint && <span className="mt-0.5 block text-[9px] normal-case text-text-muted">{hint}</span>}
        </span>
        <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusTone(status)} rounded-sm`}>
          {status === 'not-applicable' ? 'n/a' : status}
        </span>
      </summary>
      <div className="border-t border-border p-3">{children}</div>
    </details>
  );
}

function number(value: number, maximumFractionDigits = 1): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value);
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element {
  return (
    <label className="block min-w-0 text-[9px] font-bold uppercase tracking-wider text-text-muted">
      {label}
      {children}
      {hint && <span className="mt-1 block normal-case font-normal tracking-normal">{hint}</span>}
    </label>
  );
}

const controlClass = 'mt-1 w-full min-w-0 max-w-full border border-border bg-background px-2 py-1.5 text-[11px] font-normal normal-case tracking-normal text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60';

function EnvelopeFields({
  envelopeKey,
  value,
  disabled,
  onChange,
}: {
  envelopeKey: EnvelopeKey;
  value: { lengthM: string; widthM: string };
  disabled: boolean;
  onChange: (next: { lengthM: string; widthM: string }) => void;
}): React.JSX.Element {
  const label = envelopeLabels[envelopeKey];
  return (
    <fieldset className="grid min-w-0 grid-cols-2 items-end gap-2 border-b border-border/60 py-2 last:border-b-0 sm:grid-cols-[minmax(7.5rem,1fr)_5.5rem_5.5rem]" data-envelope-key={envelopeKey}>
      <legend className="sr-only">{label} dimensions</legend>
      <span className="col-span-2 min-w-0 pb-0.5 text-[10px] text-text-secondary sm:col-span-1 sm:pb-1.5">{label}</span>
      <Field label="Length · m">
        <input
          aria-label={`${label} length (m)`}
          className={controlClass}
          disabled={disabled}
          inputMode="decimal"
          min="0"
          onChange={event => onChange({ ...value, lengthM: event.target.value })}
          type="number"
          value={value.lengthM}
        />
      </Field>
      <Field label="Width · m">
        <input
          aria-label={`${label} width (m)`}
          className={controlClass}
          disabled={disabled}
          inputMode="decimal"
          min="0"
          onChange={event => onChange({ ...value, widthM: event.target.value })}
          type="number"
          value={value.widthM}
        />
      </Field>
    </fieldset>
  );
}

function draftCompleteness(
  form: DutyForm,
  ccsEnabled: boolean,
): GenerationProjectDutyCompleteness {
  const envelope = form.envelopes;
  const present = (value: { lengthM: string; widthM: string }) => (
    Boolean(value.lengthM.trim() && value.widthM.trim())
  );
  const performanceValues = [
    form.referenceIsoGrossMWPerBlock,
    form.ambientCurve,
    form.degradationPct,
    form.auxiliaryLoadMWPerBlock,
    form.heatRateBtuPerKWhHhv,
  ];
  const suppliedPerformance = performanceValues.every(value => value.trim());
  const anyPerformance = performanceValues.some(value => value.trim());
  const ccsCount = [
    envelope.ccsCaptureEnvelope,
    envelope.ccsCompressionEnvelope,
    envelope.co2ExportRouteEnvelope,
  ].filter(present).length;
  const statuses: GenerationProjectDutyCompleteness['statuses'] = {
    ambientPerformance: suppliedPerformance ? 'supplied' : anyPerformance ? 'partial' : 'missing',
    fuelGasPressure: form.gasMinimumRequiredKPa && form.gasSiteSupplyMinimumKPa
      ? 'supplied'
      : form.gasMinimumRequiredKPa ? 'partial' : 'missing',
    acousticSource: form.acousticMode === 'none' ? 'missing' : 'supplied',
    waterBalance: form.waterMode === 'none' ? 'missing' : 'supplied',
    installedPlant: present(envelope.generationBlockEnvelope)
      ? form.maintenanceClearanceIncluded
        && form.fireAccessIncluded
        && present(envelope.coolingEnvelope)
        && present(envelope.switchyardEnvelope)
        ? 'supplied'
        : 'partial'
      : 'missing',
    naturalGasRoute: present(envelope.naturalGasRouteEnvelope) ? 'supplied' : 'missing',
    ccsIntegration: !ccsEnabled
      ? 'not-applicable'
      : ccsCount === 3 ? 'supplied' : ccsCount > 0 ? 'partial' : 'missing',
    reliability: form.forcedOutagePct ? 'supplied' : 'missing',
  };
  const entries = Object.entries(statuses) as Array<[
    keyof typeof statuses,
    GenerationProjectDutyEvidenceStatus,
  ]>;
  return {
    statuses,
    suppliedCount: entries.filter(([, status]) => status === 'supplied').length,
    totalCount: entries.filter(([, status]) => status !== 'not-applicable').length,
    missingItems: entries
      .filter(([, status]) => status !== 'supplied' && status !== 'not-applicable')
      .map(([key, status]) => `${key} (${status})`),
  };
}

/**
 * Project-specific OEM/EPC duty editor. The component captures only
 * coordinate-free engineering authority; application and persistence remain
 * controlled by the parent workbench.
 */
export function GenerationProjectDutyBoard({
  input,
  designDryBulbC,
  designDryBulbBasis = 'sed-peak',
  documents = [],
  documentPrefillEnabled = false,
  evidenceMode,
  readOnly = false,
  actionsDisabled = false,
  appliedStatus = null,
  onApply,
  onClear,
  onOpenDocuments,
  onDraftDirtyChange,
  scenarioResetKey = null,
  embedded = false,
}: GenerationProjectDutyBoardProps): React.JSX.Element {
  const [form, setForm] = useState<DutyForm>(() => formFromInput(input));
  const [baselineForm, setBaselineForm] = useState<DutyForm>(() => formFromInput(input));
  const [error, setError] = useState<string | null>(null);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [openSourceRows, setOpenSourceRows] = useState<ReadonlySet<number>>(
    () => new Set([0]),
  );
  const active = useMemo(() => projectGenerationProjectDutyExhibitBasis(input), [input]);
  const draftEvidenceCompleteness = useMemo(
    () => draftCompleteness(form, input.ccs.enabled),
    [form, input.ccs.enabled],
  );
  /**
   * The MINIMUM set the validator will accept, checked live.
   *
   * A dossier needs six authored values; everything else on this form is
   * optional. Until now the only way to discover that was to press Apply and
   * read a thrown `RangeError`, one failure at a time. This states the
   * remaining set up front, in the same order the fields appear.
   *
   * Deliberately a PRESENTATION mirror of `validateGenerationProjectDutyBasis`
   * — it never gates Apply (the library validator stays the only authority), so
   * a disagreement shows up as a stale hint, never as a false block.
   */
  const requiredGaps = useMemo((): readonly string[] => {
    const gaps: string[] = [];
    if (!form.basisName.trim()) gaps.push('Dossier name');
    const source = form.sources[0];
    if (!source) {
      gaps.push('One evidence source');
    } else {
      if (!source.authorityRole.trim()) gaps.push('Source 1 authority');
      if (!source.publisher.trim()) gaps.push('Source 1 publisher');
      if (!source.title.trim()) gaps.push('Source 1 title');
      if (source.disclosure === 'public') {
        if (!source.url.trim().startsWith('https://')) gaps.push('Source 1 HTTPS URL');
      } else {
        if (!source.fileName.trim()) gaps.push('Source 1 file name');
        if (!/^[a-f\d]{64}$/i.test(source.sha256.trim())) gaps.push('Source 1 SHA-256');
      }
    }
    const block = form.envelopes.generationBlockEnvelope;
    if (!block.lengthM.trim() || !block.widthM.trim()) gaps.push('Generation block L × W');
    return gaps;
  }, [form.basisName, form.envelopes.generationBlockEnvelope, form.sources]);

  // How many of the seven OPTIONAL installed envelopes carry dimensions —
  // drives the collapsed group's summary so "none supplied" is never ambiguous
  // with "hidden".
  const optionalEnvelopesSupplied = useMemo(
    () => OPTIONAL_ENVELOPE_KEYS.filter(key => (
      form.envelopes[key].lengthM.trim() !== '' || form.envelopes[key].widthM.trim() !== ''
    )).length,
    [form.envelopes],
  );
  const draftDirty = useMemo(
    () => !sameDutyForm(form, baselineForm),
    [baselineForm, form],
  );
  const disabled = readOnly || actionsDisabled;
  const bound = boundEquipment(input);
  const editorDisabled = disabled || !bound;
  const equipmentRevision = equipmentBindingRevision(input);
  const projectDutyRevision = appliedProjectDutyRevision(input);
  const documentCandidates = useMemo(
    () => listGenerationDocumentEvidenceCandidates({
      evidenceMode,
      documents: [...documents],
    }),
    [documents, evidenceMode],
  );
  const documentPrefill = useMemo(
    () => resolveProjectDutyDocumentPrefill({
      input,
      documents,
      ...(documentPrefillEnabled && evidenceMode === 'docs-included'
        ? { evidenceMode: 'docs-included' as const }
        : {}),
    }),
    [documentPrefillEnabled, documents, evidenceMode, input],
  );
  const documentPrefillSignature = JSON.stringify(documentPrefill);

  useEffect(() => {
    const nextForm = formFromInput(input);
    setForm(nextForm);
    setBaselineForm(nextForm);
    setError(null);
    setOpenSourceRows(new Set([0]));
    setDocumentPickerOpen(false);
    // scenarioResetKey is intentionally in the dependency list so that switching to
    // a different scenario with identical equipment/dossier still clears the draft.
  }, [equipmentRevision, projectDutyRevision, scenarioResetKey]);

  useEffect(() => {
    if (!documentPrefillEnabled) return;
    setForm(current => {
      const merged = mergeDocumentPrefill(current, documentPrefill);
      return sameDutyForm(current, merged) ? current : merged;
    });
  }, [
    documentPrefill,
    documentPrefillEnabled,
    documentPrefillSignature,
    equipmentRevision,
    projectDutyRevision,
    scenarioResetKey,
  ]);

  useEffect(() => {
    onDraftDirtyChange?.(draftDirty);
    // UNMOUNT RESET — the board only mounts while a catalog envelope is the
    // working basis. Applying a user-supplied entry, changing technology or
    // ISO-gross, or applying a planning alternative all clear `equipmentBasis`
    // and unmount this board. Without this cleanup the last `true` stuck on the
    // parent forever: save, promote, publish and SED rebuild were all refused
    // by `guardProjectDutyDraft`, and the guard's own remedy — scroll to the
    // dossier editor and Apply or Discard — pointed at a surface that no longer
    // rendered either control. There was no way out of that state in the UI.
    //
    // A discarded draft is the correct semantics here: the dossier's binding
    // target is gone, so its unapplied edits can no longer be applied to
    // anything. The applied dossier (if any) was already cleared by the same
    // apply-path that unmounted us.
    return () => onDraftDirtyChange?.(false);
  }, [draftDirty, onDraftDirtyChange]);

  const updateEnvelope = (
    envelopeKey: EnvelopeKey,
    value: { lengthM: string; widthM: string },
  ) => {
    setForm(current => ({
      ...current,
      envelopes: { ...current.envelopes, [envelopeKey]: value },
    }));
  };

  const updateSource = (index: number, patch: Partial<SourceForm>) => {
    setForm(current => ({
      ...current,
      sources: current.sources.map((source, sourceIndex) => (
        sourceIndex === index ? { ...source, ...patch } : source
      )),
    }));
  };

  const linkDocument = (candidate: GenerationDocumentEvidenceCandidate) => {
    if (!candidate.eligible || !candidate.draft) return;
    const digest = candidate.draft.sha256.toLowerCase();
    if (form.sources.some(source => source.sha256.toLowerCase() === digest)) {
      setError(`${candidate.fileName} is already linked to this dossier.`);
      return;
    }
    const linkedSource: SourceForm = {
      ...emptySource(form.sources.length + 1),
      id: candidate.draft.id,
      disclosure: 'private',
      authorityRole: '',
      title: candidate.draft.title,
      publisher: candidate.draft.publisher,
      fileName: candidate.draft.fileName,
      sha256: candidate.draft.sha256,
      siteDocumentId: candidate.draft.siteDocumentId ?? '',
    };
    const pristineIndex = form.sources.findIndex(isPristineSource);
    setForm(current => {
      if (pristineIndex < 0) {
        return { ...current, sources: [...current.sources, linkedSource] };
      }
      const previousId = current.sources[pristineIndex]?.id ?? '';
      return {
        ...current,
        sources: current.sources.map((source, index) => (
          index === pristineIndex ? linkedSource : source
        )),
        performanceSourceIds: replaceSourceRef(current.performanceSourceIds, previousId, linkedSource.id),
        installedSourceIds: replaceSourceRef(current.installedSourceIds, previousId, linkedSource.id),
        gasSourceIds: replaceSourceRef(current.gasSourceIds, previousId, linkedSource.id),
        waterSourceIds: replaceSourceRef(current.waterSourceIds, previousId, linkedSource.id),
        acousticSourceIds: replaceSourceRef(current.acousticSourceIds, previousId, linkedSource.id),
      };
    });
    const linkedIndex = pristineIndex < 0 ? form.sources.length : pristineIndex;
    setOpenSourceRows(current => new Set([...current, linkedIndex]));
    setDocumentPickerOpen(false);
    setError(null);
  };

  const removeSource = (sourceIndex: number) => {
    setForm(current => {
      const previousId = current.sources[sourceIndex]?.id ?? '';
      const remaining = current.sources.filter((_, index) => index !== sourceIndex);
      const nextSources = remaining.length > 0 ? remaining : [emptySource(1)];
      const replacementId = remaining.length > 0 ? '' : nextSources[0]!.id;
      return {
        ...current,
        sources: nextSources,
        performanceSourceIds: replaceSourceRef(current.performanceSourceIds, previousId, replacementId),
        installedSourceIds: replaceSourceRef(current.installedSourceIds, previousId, replacementId),
        gasSourceIds: replaceSourceRef(current.gasSourceIds, previousId, replacementId),
        waterSourceIds: replaceSourceRef(current.waterSourceIds, previousId, replacementId),
        acousticSourceIds: replaceSourceRef(current.acousticSourceIds, previousId, replacementId),
      };
    });
    setOpenSourceRows(new Set([0]));
    setError(null);
  };

  const buildDraft = (): GenerationProjectDutyBasisDraft => {
    if (!bound) {
      throw new RangeError('Apply an equipment envelope — catalog or user-supplied — before the project duty dossier.');
    }
    if (!form.basisId.trim() || !form.basisName.trim()) {
      throw new RangeError('Dossier ID and name are required.');
    }
    // CODEPOINT order, matching `sortedUnique` in generationProjectDuty.ts —
    // NOT localeCompare. Source ids permit mixed case, and the two orders
    // disagree there: localeCompare puts ['apple-spec','Banana-spec'] in that
    // order, while the validator requires 'apple-spec' < 'Banana-spec', which
    // is false. The dossier then failed to apply with "sources must be sorted
    // by id" — a rule the user cannot act on, since the list is not reorderable
    // from the UI. Same comparator as `parseSourceRefs`.
    const sources = [...form.sources.map(sourceFromForm)]
      .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
    if (new Set(sources.map(source => source.id)).size !== sources.length) {
      throw new RangeError('Evidence source IDs must be unique.');
    }
    const availableSourceIds = new Set(sources.map(source => source.id));
    const ambientPoints = parseAmbientCurve(form.ambientCurve);
    const performancePresent = Boolean(
      form.referenceIsoGrossMWPerBlock.trim()
      || ambientPoints
      || form.degradationPct.trim()
      || form.auxiliaryLoadMWPerBlock.trim()
      || form.heatRateBtuPerKWhHhv.trim()
      || form.forcedOutagePct.trim(),
    );
    const generationBlock = optionalEnvelope(
      form.envelopes.generationBlockEnvelope,
      envelopeLabels.generationBlockEnvelope,
    );
    if (!generationBlock) {
      throw new RangeError('Installed generation block length and width are required.');
    }
    const blockQuantity = input.units.reduce((sum, unit) => sum + unit.count, 0);
    if (!Number.isInteger(blockQuantity) || blockQuantity < 1) {
      throw new RangeError('Scenario physical block quantity must be a positive integer.');
    }
    const installedSourceRefs = parseSourceRefs(
      form.installedSourceIds,
      availableSourceIds,
      'Installed reservations',
    );
    const draft: GenerationProjectDutyBasisDraft = {
      basisId: form.basisId.trim(),
      basisName: form.basisName.trim(),
      ...(performancePresent ? {
        performance: {
          sourceIds: parseSourceRefs(
            form.performanceSourceIds,
            availableSourceIds,
            'Ambient performance',
          ),
          ...(numberOrUndefined(form.referenceIsoGrossMWPerBlock) == null ? {} : {
            referenceIsoGrossMWPerBlock: numberOrUndefined(form.referenceIsoGrossMWPerBlock),
          }),
          ...(ambientPoints ? {
            ambientCorrection: {
              input: 'dry-bulb-c',
              output: 'gross-output-factor',
              interpolation: 'piecewise-linear-no-extrapolation',
              designDryBulbC,
              points: ambientPoints,
              appliedGrossOutputFactor: interpolateAmbientOutputFactor(
                ambientPoints,
                designDryBulbC,
              ),
            },
          } : {}),
          ...(numberOrUndefined(form.degradationPct) == null ? {} : {
            degradationFactor: numberOrUndefined(form.degradationPct)! / 100,
          }),
          ...(numberOrUndefined(form.auxiliaryLoadMWPerBlock) == null ? {} : {
            auxiliaryLoadMWPerBlock: numberOrUndefined(form.auxiliaryLoadMWPerBlock),
          }),
          ...(numberOrUndefined(form.heatRateBtuPerKWhHhv) == null ? {} : {
            heatRateBtuPerKWhHhv: numberOrUndefined(form.heatRateBtuPerKWhHhv),
          }),
          ...(numberOrUndefined(form.forcedOutagePct) == null ? {} : {
            forcedOutageRate: numberOrUndefined(form.forcedOutagePct)! / 100,
          }),
          referenceConditions: {
            ...(numberOrUndefined(form.elevationM) == null ? {} : {
              elevationM: numberOrUndefined(form.elevationM),
            }),
            ...(numberOrUndefined(form.relativeHumidityPct) == null ? {} : {
              relativeHumidityPct: numberOrUndefined(form.relativeHumidityPct),
            }),
            ...(numberOrUndefined(form.barometricPressureKPaAbsolute) == null ? {} : {
              barometricPressureKPaAbsolute: numberOrUndefined(
                form.barometricPressureKPaAbsolute,
              ),
            }),
            notes: form.referenceConditionNotes.trim() || 'Project source reference conditions.',
          },
          normalizationNote: form.normalizationNote.trim()
            || 'Applied directly from the identified project duty source.',
        },
      } : {}),
      installedReservations: {
        blockQuantity,
        generationBlockEnvelope: {
          ...generationBlock,
          scope: 'installed-operating-envelope',
          maintenanceClearanceIncluded: form.maintenanceClearanceIncluded,
          fireAccessIncluded: form.fireAccessIncluded,
        },
        ...Object.fromEntries(([
          'coolingEnvelope',
          'switchyardEnvelope',
          'ccsCaptureEnvelope',
          'ccsCompressionEnvelope',
          'naturalGasRouteEnvelope',
          'co2ExportRouteEnvelope',
          'constructionLaydownEnvelope',
        ] as const).flatMap(envelopeKey => {
          const envelope = optionalEnvelope(form.envelopes[envelopeKey], envelopeLabels[envelopeKey]);
          return envelope ? [[envelopeKey, envelope]] : [];
        })),
        sourceIds: installedSourceRefs,
      },
      sources,
    };

    const gasMinimum = numberOrUndefined(form.gasMinimumRequiredKPa);
    const gasMaximum = numberOrUndefined(form.gasMaximumAllowedKPa);
    const gasSupply = numberOrUndefined(form.gasSiteSupplyMinimumKPa);
    const gasSiteMaximum = numberOrUndefined(form.gasSiteSupplyMaximumKPa);
    if (gasMinimum != null || gasMaximum != null || gasSupply != null || gasSiteMaximum != null) {
      if (gasMinimum == null) throw new RangeError('Fuel-gas minimum required pressure is required.');
      draft.fuelGas = {
        sourceIds: parseSourceRefs(
          form.gasSourceIds,
          availableSourceIds,
          'Fuel-gas interface',
        ),
        minimumRequired: {
          valueKPa: gasMinimum,
          reference: form.gasMinimumRequiredReference,
        },
        ...(gasMaximum == null ? {} : {
          maximumAllowed: {
            valueKPa: gasMaximum,
            reference: form.gasMaximumAllowedReference,
          },
        }),
        ...(gasSupply == null ? {} : {
          documentedSiteSupplyMinimum: {
            valueKPa: gasSupply,
            reference: form.gasSiteSupplyMinimumReference,
          },
        }),
        ...(gasSiteMaximum == null ? {} : {
          documentedSiteSupplyMaximum: {
            valueKPa: gasSiteMaximum,
            reference: form.gasSiteSupplyMaximumReference,
          },
        }),
      };

      // All-or-nothing: the derate is not computable from a subset, so a
      // half-filled interconnect record must not be constructible.
      if (form.gasSupplyQuantityBasis !== '') {
        const station = numberOrUndefined(form.gasSupplyStationCapacity);
        const ratedAt = numberOrUndefined(form.gasSupplyRatedAtPressureKPa);
        if (station == null) throw new RangeError('Interconnect station design capacity is required.');
        if (ratedAt == null) throw new RangeError('The pressure the interconnect capacity was rated at is required.');
        if (!form.gasSupplyOperatingCase.trim()) throw new RangeError('Interconnect operating case is required.');
        const zRated = numberOrUndefined(form.gasSupplyZAtRated);
        const zMinimum = numberOrUndefined(form.gasSupplyZAtMinimum);
        if ((zRated == null) !== (zMinimum == null)) {
          throw new RangeError('Document compressibility at BOTH the rated and minimum delivery pressures, or neither.');
        }
        let capacityBasis: GenerationProjectFuelGasSupplyDuty['capacityBasis'];
        if (form.gasSupplyCapacityKind === 'energy') {
          capacityBasis = { kind: 'energy' };
        } else {
          const hhv = numberOrUndefined(form.gasSupplyHhvBtuPerScf);
          const basePressure = numberOrUndefined(form.gasSupplyBasePressureKPaAbsolute);
          const baseTemperature = numberOrUndefined(form.gasSupplyBaseTemperatureC);
          // Without a documented HHV the volumetric conversion is an assumption,
          // which is precisely what the automatic prefill path refuses to make.
          if (hhv == null) throw new RangeError('A documented heating value is required to convert a volumetric gas capacity.');
          if (basePressure == null || baseTemperature == null) {
            throw new RangeError('Standard base pressure and temperature are required for a volumetric gas capacity.');
          }
          capacityBasis = {
            kind: 'standard-volume',
            documentedHhvBtuPerScf: hhv,
            standardConditions: {
              basePressureKPaAbsolute: basePressure,
              baseTemperatureC: baseTemperature,
            },
          };
        }
        draft.fuelGas.supply = {
          quantityBasis: form.gasSupplyQuantityBasis,
          capacityBasis,
          stationDesignCapacity: station,
          ...(numberOrUndefined(form.gasSupplyContractMaxDailyQuantity) == null ? {} : {
            contractMaxDailyQuantity: numberOrUndefined(form.gasSupplyContractMaxDailyQuantity)!,
          }),
          ratedAtPressure: {
            valueKPa: ratedAt,
            reference: form.gasSupplyRatedAtPressureReference,
          },
          capacityLimit: 'velocity-limited-meter-run',
          ...(zRated == null || zMinimum == null ? {} : {
            compressibility: { atRatedPressure: zRated, atMinimumDeliveryPressure: zMinimum },
          }),
          operatingCase: form.gasSupplyOperatingCase.trim(),
          sourceIds: draft.fuelGas.sourceIds,
        };
      }
    }

    if (form.waterMode !== 'none') {
      const withdrawal = requiredNumber(form.waterWithdrawal, 'Water withdrawal', -1);
      const consumption = requiredNumber(form.waterConsumption, 'Water consumption', -1);
      if (withdrawal < consumption) {
        throw new RangeError('Water withdrawal cannot be below water consumption.');
      }
      draft.water = form.waterMode === 'gross-output-intensity'
        ? {
            kind: 'gross-output-intensity',
            withdrawalGalPerMWhGross: withdrawal,
            consumptionGalPerMWhGross: consumption,
            operatingCase: form.waterOperatingCase.trim() || 'Project design-day duty',
            sourceIds: parseSourceRefs(
              form.waterSourceIds,
              availableSourceIds,
              'Generation water',
            ),
          }
        : {
            kind: 'absolute-design-day',
            withdrawalGalPerDay: withdrawal,
            consumptionGalPerDay: consumption,
            referenceGrossMWhPerDay: requiredNumber(
              form.waterReferenceGrossMWhPerDay,
              'Reference gross generation',
            ),
            operatingCase: form.waterOperatingCase.trim() || 'Project design-day duty',
            sourceIds: parseSourceRefs(
              form.waterSourceIds,
              availableSourceIds,
              'Generation water',
            ),
          };
    }

    if (form.acousticMode !== 'none') {
      if (!form.acousticLabel.trim() || !form.acousticOperatingCase.trim()) {
        throw new RangeError('Acoustic source label and operating case are required.');
      }
      const levelDbA = requiredNumber(form.acousticLevelDbA, 'Acoustic level', -1);
      const appliedDatum = form.acousticMode === 'sound-power'
        ? {
            kind: 'sound-power' as const,
            levelDbA,
            ...(numberOrUndefined(form.acousticDirectivityCorrectionDb) == null ? {} : {
              directivityCorrectionDb: numberOrUndefined(
                form.acousticDirectivityCorrectionDb,
              ),
            }),
          }
        : {
            kind: 'reference-spl' as const,
            levelDbA,
            referenceDistanceM: requiredNumber(
              form.acousticReferenceDistanceM,
              'Acoustic reference distance',
            ),
            scope: form.acousticReferenceScope,
            ...(form.acousticBasisLabel.trim()
              ? { basisLabel: form.acousticBasisLabel.trim() }
              : {}),
          };
      const existingAcousticSources = input.projectDutyBasis?.acoustics?.acousticSources ?? [];
      const additionalAcousticSources = structuredClone(existingAcousticSources.slice(1));
      const unresolvedAdditionalSourceIds = additionalAcousticSources.flatMap(source => (
        source.sourceIds.filter(sourceId => !availableSourceIds.has(sourceId))
      ));
      if (unresolvedAdditionalSourceIds.length > 0) {
        throw new RangeError(
          `Preserved acoustic rows reference removed evidence source ID(s): ${[...new Set(unresolvedAdditionalSourceIds)].join(', ')}.`,
        );
      }
      draft.acoustics = {
        acousticSources: [
          {
            id: existingAcousticSources[0]?.id ?? 'project-acoustic-source-1',
            label: form.acousticLabel.trim(),
            quantity: {
              scope: form.acousticQuantityScope,
              count: requiredNumber(form.acousticQuantity, 'Acoustic source quantity'),
            },
            operatingCase: form.acousticOperatingCase.trim(),
            appliedDatum,
            sourceIds: parseSourceRefs(
              form.acousticSourceIds,
              availableSourceIds,
              'Broadband acoustic source',
            ),
          },
          ...additionalAcousticSources,
        ],
      };
    }
    return draft;
  };

  const apply = () => {
    try {
      setError(null);
      onApply(buildDraft());
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  const clear = () => {
    try {
      setError(null);
      onClear();
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  // Reverts unapplied edits back to the last applied/bound baseline without
  // touching the scenario's applied dossier — the "discard" half of the
  // "apply or discard" contract the save/promote/publish guards depend on.
  const discardDraftEdits = () => {
    setError(null);
    setForm(baselineForm);
    setOpenSourceRows(new Set([0]));
    setDocumentPickerOpen(false);
  };

  return (
    <section
      aria-label="Project OEM and EPC duty dossier"
      className="min-w-0 max-w-full border-2 border-border bg-card"
      data-testid="generation-project-duty-board"
    >
      {!embedded && <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <FileCheck2 className="h-4 w-4 text-primary" />
            Project OEM / EPC duty dossier
          </h2>
          <p className="mt-1 max-w-4xl text-[10px] text-text-muted">
            Overlay project-specific performance, installed envelopes, utilities, and acoustics on the selected immutable catalog rating. All dimensions are coordinate-free reservations until placed in the parcel test fit.
          </p>
        </div>
        <div className="border border-border bg-background px-3 py-2 text-right text-[9px] uppercase tracking-wider text-text-muted rounded-md">
          <span className="block font-bold text-text-primary">Bound catalog</span>
          {bound
            ? `${bound.label}${bound.unverified ? ' · unverified' : ''}`
            : 'Select equipment first'}
        </div>
      </div>}

      <div className="border-b border-border bg-background px-4 py-3" data-testid="project-duty-evidence-rail">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-text-muted">
            Draft evidence completeness
          </p>
          <p
            className="text-[10px] tabular-nums text-text-secondary"
            data-dirty={draftDirty ? 'true' : 'false'}
            data-testid="project-duty-draft-status"
          >
            <strong className="text-text-primary">{draftEvidenceCompleteness.suppliedCount} / {draftEvidenceCompleteness.totalCount}</strong> supplied
            {!bound
              ? ' · editor locked until equipment is selected'
              : draftDirty
                ? ' · unapplied draft changes'
                : active
                  ? ' · draft matches applied dossier'
                  : ' · draft preview · not applied'}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-1 sm:grid-cols-8" role="list">
          {evidenceLabels.map(item => {
            const status = draftEvidenceCompleteness.statuses[item.key];
            return (
              <div
                aria-label={`${item.full}: ${status}`}
                className={`relative border px-2 py-2 ${statusTone(status)} rounded-md`}
                data-evidence-key={item.key}
                key={item.key}
                role="listitem"
                title={`${item.full}: ${status}`}
              >
                <span className="block font-mono text-[9px] font-bold tracking-[0.12em]">{item.short}</span>
                <span className="mt-1 block text-[9px] uppercase tracking-wide">{status}</span>
                <span aria-hidden="true" className="absolute -right-1 top-1/2 z-10 hidden h-px w-1 bg-border sm:block" />
              </div>
            );
          })}
        </div>
      </div>

      {!bound && (
        <div className="flex items-start gap-2 border-b border-warning/40 bg-warning/10 px-4 py-3 text-[10px] text-warning" role="note">
          <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
          <p><strong>Equipment binding required.</strong> Apply an equipment envelope above — catalog or user-supplied — before attaching project OEM/EPC authority.</p>
        </div>
      )}

      {active && (
        <div className="grid gap-px border-b border-border bg-border text-[10px] sm:grid-cols-2 lg:grid-cols-4" data-testid="project-duty-active-summary">
          <div className="bg-card p-3"><span className="block text-text-muted">Applied dossier</span><strong className="mt-1 block">{active.basis.basisName}</strong><span className="mt-1 block text-[9px] text-text-muted" data-testid="project-duty-applied-completeness">Applied evidence {active.completeness.suppliedCount} / {active.completeness.totalCount} · {active.alignmentStatus === 'aligned' ? 'inputs aligned' : 'modified after apply'}</span></div>
          <div className="bg-card p-3"><span className="block text-text-muted">Permanent equipment</span><strong className="mt-1 block tabular-nums">{number(active.spatialSummary.permanentEquipmentAcres)} ac</strong></div>
          <div className="bg-card p-3"><span className="block text-text-muted">Routes + laydown</span><strong className="mt-1 block tabular-nums">{number(active.spatialSummary.routeReservationAcres + active.spatialSummary.constructionLaydownAcres)} ac</strong></div>
          <div className="bg-card p-3"><span className="block text-text-muted">Gas pressure window</span><strong className={`mt-1 block uppercase ${active.gasPressureCheck.verdict === 'pass' ? 'text-success' : active.gasPressureCheck.verdict === 'fail' ? 'text-danger' : 'text-warning'}`}>{active.gasPressureCheck.verdict}{active.gasPressureCheck.marginKPa == null ? '' : ` · minimum margin ${number(active.gasPressureCheck.marginKPa)} kPa`}{active.gasPressureCheck.upperMarginKPa == null ? '' : ` · maximum screening headroom ${number(active.gasPressureCheck.upperMarginKPa)} kPa`}</strong></div>
        </div>
      )}

      {documentPrefillEnabled
        && (documentPrefill.sources.length > 0
          || documentPrefill.conflicts.length > 0
          || documentPrefill.reviewOnlyCcsFacts.length > 0) && (
        <div
          className="border-b border-primary/30 bg-primary/[0.04] px-4 py-3 text-[10px] text-text-secondary"
          data-testid="project-duty-document-prefill"
          role="note"
        >
          <p className="font-bold uppercase tracking-wider text-primary">
            Unverified document duty draft
          </p>
          <p className="mt-1">
            Exact, hash-bound facts filled empty dossier fields. Identify the issuing authority,
            review every value, then use the existing Apply workflow before any value can affect
            the solver.
          </p>
          {documentPrefill.sources.length > 0 && (
            <p className="mt-1">
              Sources:{' '}
              {documentPrefill.sources.map((source, index) => (
                <React.Fragment key={source.id}>
                  {index > 0 ? ' · ' : ''}
                  <button
                    type="button"
                    className="font-bold text-primary hover:underline"
                    onClick={() => onOpenDocuments?.(source.documentId)}
                    title={`SHA-256 ${source.sha256}${source.pages.length ? ` · page ${source.pages.join(', ')}` : ''}`}
                  >
                    {source.fileName}
                  </button>
                </React.Fragment>
              ))}
            </p>
          )}
          {documentPrefill.conflicts.map(conflict => (
            <p className="mt-1 text-warning" key={conflict}>
              <strong>Conflict:</strong> {conflict}
            </p>
          ))}
          {documentPrefill.reviewOnlyCcsFacts.length > 0 && (
            <p className="mt-1 text-warning">
              {documentPrefill.reviewOnlyCcsFacts.length} CCS process-duty fact
              {documentPrefill.reviewOnlyCcsFacts.length === 1 ? '' : 's'} remain review-only:
              the current verified dossier schema has no CCS process-duty authority member, so
              they were not written into solver inputs.
            </p>
          )}
        </div>
      )}

      {bound && (
        <div
          className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5"
          data-complete={requiredGaps.length === 0 ? 'true' : 'false'}
          data-testid="project-duty-required"
          role="status"
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-text-muted">
            {requiredGaps.length === 0 ? 'Required set complete' : 'Still required to apply'}
          </span>
          {requiredGaps.length === 0
            ? <span className="text-[10px] text-text-secondary">Everything below is optional — add only what the project has documented.</span>
            : requiredGaps.map(gap => (
              <span
                className="border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-warning rounded-sm"
                key={gap}
              >
                {gap}
              </span>
            ))}
        </div>
      )}

      <div
        aria-disabled={editorDisabled}
        className={`grid min-w-0 grid-cols-1 gap-px bg-border xl:grid-cols-[minmax(0,0.94fr)_minmax(0,1.06fr)] ${!bound ? 'opacity-60' : ''}`}
        data-testid="project-duty-editor"
      >
        <div className="min-w-0 space-y-4 bg-card p-3 sm:p-4">
          <fieldset disabled={editorDisabled}>
            <legend className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
              <Link2 className="h-3.5 w-3.5 text-primary" /> Dossier identity
            </legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Dossier ID"><input aria-label="Project duty dossier ID" className={controlClass} onChange={event => setForm(current => ({ ...current, basisId: event.target.value }))} value={form.basisId} /></Field>
              <Field label="Dossier name"><input aria-label="Project duty dossier name" className={controlClass} onChange={event => setForm(current => ({ ...current, basisName: event.target.value }))} value={form.basisName} /></Field>
            </div>
          </fieldset>

          <fieldset disabled={editorDisabled}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <legend className="text-[10px] font-bold uppercase tracking-wider">Evidence sources</legend>
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  aria-expanded={documentPickerOpen}
                  aria-label="Link evidence from Documents"
                  className="inline-flex items-center gap-1 border border-primary bg-primary/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/15 disabled:opacity-50 rounded-md"
                  onClick={() => setDocumentPickerOpen(open => !open)}
                  type="button"
                >
                  <FolderOpen className="h-3 w-3" /> Link from Documents
                </button>
                {onOpenDocuments && (
                  <button
                    className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50 rounded-md"
                    onClick={() => onOpenDocuments()}
                    type="button"
                  >
                    <FilePlus2 className="h-3 w-3" /> Upload in Documents
                  </button>
                )}
                <button
                  className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50 rounded-md"
                  onClick={() => setForm(current => ({
                    ...current,
                    sources: [...current.sources, emptySource(current.sources.length + 1)],
                  }))}
                  type="button"
                >
                  <Link2 className="h-3 w-3" /> Manual source
                </button>
              </div>
            </div>
            {documentPickerOpen && (
              <div className="mt-2 border-2 border-primary/30 bg-primary/[0.04]" data-testid="generation-document-evidence-picker">
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-primary/20 px-3 py-2">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">Documents evidence shelf</p>
                    <p className="mt-1 text-[9px] text-text-muted">The document record and its raw-file SHA-256 are frozen into the scenario. Add the actual issuing authority and publisher after linking.</p>
                  </div>
                  <span className="font-mono text-[9px] text-text-muted">{documentCandidates.filter(candidate => candidate.eligible).length} linkable</span>
                </div>
                {documentCandidates.length === 0 ? (
                  <div className="px-3 py-4 text-[10px] text-text-muted">
                    No evidence-eligible documents are available. Upload a file in Documents or enable documents for SED evidence.
                  </div>
                ) : (
                  <div className="max-h-72 divide-y divide-border overflow-y-auto">
                    {documentCandidates.map(candidate => {
                      const linked = Boolean(candidate.draft && form.sources.some(source => (
                        source.sha256.toLowerCase() === candidate.draft!.sha256.toLowerCase()
                      )));
                      return (
                        <div className="grid min-w-0 grid-cols-1 gap-2 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center" key={candidate.documentId}>
                          <div className="min-w-0">
                            <p className="truncate text-[10px] font-bold text-text-primary" title={candidate.fileName}>{candidate.fileName}</p>
                            <p className={`mt-0.5 text-[9px] ${candidate.eligible ? 'text-success' : 'text-warning'}`}>
                              {candidate.eligible
                                ? `SHA-256 available · uploaded ${new Date(candidate.uploadedAt).toLocaleDateString()}`
                                : candidate.disabledReason}
                            </p>
                          </div>
                          <button
                            className="justify-self-start border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 sm:justify-self-end rounded-md"
                            disabled={!candidate.eligible || linked}
                            onClick={() => linkDocument(candidate)}
                            type="button"
                          >
                            {linked ? 'Linked' : 'Link evidence'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="mt-2 space-y-2">
              {form.sources.map((source, index) => (
                <details
                  className="border border-border bg-background rounded-md"
                  key={`source-row-${index}`}
                  onToggle={event => {
                    const expanded = event.currentTarget.open;
                    setOpenSourceRows(current => {
                      const next = new Set(current);
                      if (expanded) next.add(index);
                      else next.delete(index);
                      return next;
                    });
                  }}
                  open={openSourceRows.has(index)}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-[10px] font-bold text-text-secondary">
                    <span className="min-w-0 truncate">{source.title || `Source ${index + 1}`} · {source.siteDocumentId ? 'Documents link' : source.disclosure}</span>
                    <span className="flex shrink-0 items-center gap-2">
                    {source.siteDocumentId && onOpenDocuments && (
                      <button
                        aria-label={`View ${source.title || `source ${index + 1}`} in Documents`}
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                        onClick={event => {
                          event.preventDefault();
                          onOpenDocuments(source.siteDocumentId);
                        }}
                        type="button"
                      >View <ExternalLink className="h-3 w-3" /></button>
                    )}
                    {(form.sources.length > 1 || source.siteDocumentId) && (
                      <button
                        aria-label={source.siteDocumentId ? `Unlink source ${index + 1}` : `Remove source ${index + 1}`}
                        className="text-text-muted hover:text-danger"
                        onClick={event => {
                          event.preventDefault();
                          removeSource(index);
                        }}
                        type="button"
                      ><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                    </span>
                  </summary>
                  <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2">
                    {source.siteDocumentId && (
                      <div className="border border-success/30 bg-success/5 px-2 py-1.5 text-[9px] text-text-secondary sm:col-span-2 rounded-md" role="note">
                        <strong className="text-success">Document linked.</strong> Record <span className="font-mono">{source.siteDocumentId}</span> is pinned to the SHA-256 below. Enter the real source authority and publisher; Deep Atomic does not infer them from the filename.
                      </div>
                    )}
                    <Field label="Source ID"><input aria-label={`Source ${index + 1} ID`} className={controlClass} onChange={event => updateSource(index, { id: event.target.value })} readOnly={Boolean(source.siteDocumentId)} value={source.id} /></Field>
                    <Field label="Disclosure"><select aria-label={`Source ${index + 1} disclosure`} className={controlClass} onChange={event => updateSource(index, { disclosure: event.target.value as SourceForm['disclosure'] })} value={source.disclosure}><option value="private">Private project document</option><option value="public">Public technical source</option></select></Field>
                    <Field label="Authority"><select aria-label={`Source ${index + 1} authority`} className={controlClass} onChange={event => updateSource(index, { authorityRole: event.target.value as SourceForm['authorityRole'] })} value={source.authorityRole}><option value="">Select authority…</option>{authorityRoles.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}</select></Field>
                    <Field label="Publisher" hint={source.siteDocumentId ? 'Enter the actual issuing organisation; it is not inferred.' : undefined}><input aria-label={`Source ${index + 1} publisher`} className={controlClass} onChange={event => updateSource(index, { publisher: event.target.value })} value={source.publisher} /></Field>
                    <Field label="Title"><input aria-label={`Source ${index + 1} title`} className={controlClass} onChange={event => updateSource(index, { title: event.target.value })} value={source.title} /></Field>
                    <Field label="Revision"><input aria-label={`Source ${index + 1} revision`} className={controlClass} onChange={event => updateSource(index, { revision: event.target.value })} value={source.revision} /></Field>
                    <Field label="Issued"><input aria-label={`Source ${index + 1} issued date`} className={controlClass} onChange={event => updateSource(index, { issuedAt: event.target.value })} type="date" value={source.issuedAt} /></Field>
                    {source.disclosure === 'public' ? <>
                      <Field label="HTTPS source"><input aria-label={`Source ${index + 1} public URL`} className={controlClass} onChange={event => updateSource(index, { url: event.target.value })} type="url" value={source.url} /></Field>
                      <Field label="Retrieved"><input aria-label={`Source ${index + 1} retrieval date`} className={controlClass} onChange={event => updateSource(index, { retrievedAt: event.target.value })} type="date" value={source.retrievedAt} /></Field>
                    </> : <>
                      <Field label="Inert file name"><input aria-label={`Source ${index + 1} private file name`} className={controlClass} onChange={event => updateSource(index, { fileName: event.target.value })} readOnly={Boolean(source.siteDocumentId)} value={source.fileName} /></Field>
                      <Field label="SHA-256"><input aria-label={`Source ${index + 1} SHA-256`} className={`${controlClass} font-mono`} onChange={event => updateSource(index, { sha256: event.target.value })} readOnly={Boolean(source.siteDocumentId)} spellCheck={false} value={source.sha256} /></Field>
                    </>}
                  </div>
                </details>
              ))}
            </div>
          </fieldset>

          <OptionalGroup hint="Project ambient curve, degradation, auxiliaries, heat rate and forced-outage rate." status={draftEvidenceCompleteness.statuses.ambientPerformance} testId="project-duty-group-performance" title="Ambient performance">
          <fieldset disabled={editorDisabled}>
            <legend className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider"><Gauge className="h-3.5 w-3.5 text-primary" /> Ambient performance</legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Performance source IDs" hint="Comma-separated evidence source IDs."><input aria-label="Project performance source IDs" className={controlClass} onChange={event => setForm(current => ({ ...current, performanceSourceIds: event.target.value }))} value={form.performanceSourceIds} /></Field>
              <Field label="Reference ISO gross · MW / block"><input aria-label="Project reference ISO gross per block (MW)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, referenceIsoGrossMWPerBlock: event.target.value }))} type="number" value={form.referenceIsoGrossMWPerBlock} /></Field>
              <div className="border border-border bg-background px-2 py-1.5 text-[9px] text-text-muted rounded-md"><span className="block font-bold uppercase tracking-wider">Site design dry bulb</span><strong className="mt-1 block text-[11px] text-text-primary" data-basis={designDryBulbBasis}>{Number.isFinite(designDryBulbC) ? `${number(designDryBulbC)} °C` : 'Missing from SED'}</strong>{designDryBulbBasis === 'monthly-maximum' && <span className="mt-0.5 block normal-case">Hottest monthly maximum — no recorded site peak</span>}</div>
              <Field label="Ambient curve" hint="One row per point: dry bulb °C, gross output factor. No extrapolation."><textarea aria-label="Project ambient correction curve" className={`${controlClass} min-h-24 font-mono`} onChange={event => setForm(current => ({ ...current, ambientCurve: event.target.value }))} placeholder={'15, 1.00\n30, 0.94\n45, 0.84'} value={form.ambientCurve} /></Field>
              <div className="grid min-w-0 content-start grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Degradation · %"><input aria-label="Project degradation factor (%)" className={controlClass} max="100" min="0.0001" onChange={event => setForm(current => ({ ...current, degradationPct: event.target.value }))} type="number" value={form.degradationPct} /></Field>
                <Field label="Auxiliary · MW / block"><input aria-label="Project auxiliary load per block (MW)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, auxiliaryLoadMWPerBlock: event.target.value }))} type="number" value={form.auxiliaryLoadMWPerBlock} /></Field>
                <Field label="Heat rate · Btu/kWh HHV"><input aria-label="Project heat rate (Btu per kWh HHV)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, heatRateBtuPerKWhHhv: event.target.value }))} type="number" value={form.heatRateBtuPerKWhHhv} /></Field>
                <Field label="Forced outage · %"><input aria-label="Project forced outage rate (%)" className={controlClass} min="0" max="100" onChange={event => setForm(current => ({ ...current, forcedOutagePct: event.target.value }))} type="number" value={form.forcedOutagePct} /></Field>
              </div>
              <Field label="Reference-condition notes"><textarea aria-label="Project performance reference-condition notes" className={`${controlClass} min-h-16`} onChange={event => setForm(current => ({ ...current, referenceConditionNotes: event.target.value }))} value={form.referenceConditionNotes} /></Field>
              <Field label="Normalization disclosure"><textarea aria-label="Project performance normalization note" className={`${controlClass} min-h-16`} onChange={event => setForm(current => ({ ...current, normalizationNote: event.target.value }))} value={form.normalizationNote} /></Field>
              <Field label="Elevation · m"><input aria-label="Project reference elevation (m)" className={controlClass} onChange={event => setForm(current => ({ ...current, elevationM: event.target.value }))} type="number" value={form.elevationM} /></Field>
              <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="RH · %"><input aria-label="Project reference relative humidity (%)" className={controlClass} min="0" max="100" onChange={event => setForm(current => ({ ...current, relativeHumidityPct: event.target.value }))} type="number" value={form.relativeHumidityPct} /></Field>
                <Field label="Barometer · kPa abs"><input aria-label="Project reference barometric pressure (kPa absolute)" className={controlClass} max="200" min="1" onChange={event => setForm(current => ({ ...current, barometricPressureKPaAbsolute: event.target.value }))} type="number" value={form.barometricPressureKPaAbsolute} /></Field>
              </div>
            </div>
          </fieldset>
          </OptionalGroup>
        </div>

        <div className="min-w-0 space-y-4 bg-card p-3 sm:p-4">
          <fieldset disabled={editorDisabled}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <legend className="text-[10px] font-bold uppercase tracking-wider">Installed reservations</legend>
              <span className="text-[9px] text-text-muted">{input.units.reduce((sum, unit) => sum + unit.count, 0)} physical block(s) · dimensions in metres</span>
            </div>
            {/* The generation-block envelope is the ONLY required dimension —
                the other seven are optional reservations that most screening
                dossiers leave open, so they no longer compete with it. */}
            <div className="mt-2 border-y border-border">
              <EnvelopeFields disabled={editorDisabled} envelopeKey="generationBlockEnvelope" onChange={value => updateEnvelope('generationBlockEnvelope', value)} value={form.envelopes.generationBlockEnvelope} />
            </div>
            <details className="mt-3 border border-border rounded-md" data-testid="project-duty-optional-envelopes" open={optionalEnvelopesSupplied > 0}>
              <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-primary">Other installed reservations</span>
                <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${optionalEnvelopesSupplied > 0 ? statusTone('partial') : statusTone('missing')} rounded-sm`}>
                  {optionalEnvelopesSupplied > 0 ? `${optionalEnvelopesSupplied} of ${OPTIONAL_ENVELOPE_KEYS.length} supplied` : 'none supplied'}
                </span>
              </summary>
              <div className="border-t border-border">
                {OPTIONAL_ENVELOPE_KEYS.map(envelopeKey => (
                  <EnvelopeFields disabled={editorDisabled} envelopeKey={envelopeKey} key={envelopeKey} onChange={value => updateEnvelope(envelopeKey, value)} value={form.envelopes[envelopeKey]} />
                ))}
              </div>
            </details>
            <div className="mt-3 max-w-md"><Field label="Installed-reservation source IDs" hint="Comma-separated evidence source IDs."><input aria-label="Project installed reservation source IDs" className={controlClass} onChange={event => setForm(current => ({ ...current, installedSourceIds: event.target.value }))} value={form.installedSourceIds} /></Field></div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <label className="flex items-start gap-2 text-[10px] text-text-secondary"><input aria-label="Installed envelope includes maintenance clearance" checked={form.maintenanceClearanceIncluded} className="mt-0.5" onChange={event => setForm(current => ({ ...current, maintenanceClearanceIncluded: event.target.checked }))} type="checkbox" /><span><strong className="block text-text-primary">Maintenance clearance included</strong>Crane access and major-component removal envelope.</span></label>
              <label className="flex items-start gap-2 text-[10px] text-text-secondary"><input aria-label="Installed envelope includes fire access" checked={form.fireAccessIncluded} className="mt-0.5" onChange={event => setForm(current => ({ ...current, fireAccessIncluded: event.target.checked }))} type="checkbox" /><span><strong className="block text-text-primary">Fire access included</strong>Fire lane and emergency response access.</span></label>
            </div>
          </fieldset>

          <div className="grid gap-4 border-t border-border pt-4 md:grid-cols-2">
            <OptionalGroup hint="OEM pressure window and the documented site supply minimum." status={draftEvidenceCompleteness.statuses.fuelGasPressure} testId="project-duty-group-gas" title="Fuel-gas interface">
            <fieldset disabled={editorDisabled}>
              <legend className="text-[10px] font-bold uppercase tracking-wider">Fuel-gas interface</legend>
              <div className="mt-3 space-y-3">
                <Field label="Gas source IDs" hint="Comma-separated evidence source IDs."><input aria-label="Project gas source IDs" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSourceIds: event.target.value }))} value={form.gasSourceIds} /></Field>
                <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="OEM minimum · kPa"><input aria-label="Project gas minimum required pressure (kPa)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasMinimumRequiredKPa: event.target.value }))} type="number" value={form.gasMinimumRequiredKPa} /></Field>
                  <Field label="Minimum reference"><select aria-label="Project gas minimum pressure reference" className={controlClass} onChange={event => setForm(current => ({ ...current, gasMinimumRequiredReference: event.target.value as DutyForm['gasMinimumRequiredReference'] }))} value={form.gasMinimumRequiredReference}><option value="gauge">Gauge · kPa(g)</option><option value="absolute">Absolute · kPa(a)</option></select></Field>
                  <Field label="OEM maximum · kPa"><input aria-label="Project gas maximum allowed pressure (kPa)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasMaximumAllowedKPa: event.target.value }))} type="number" value={form.gasMaximumAllowedKPa} /></Field>
                  <Field label="Maximum reference"><select aria-label="Project gas maximum pressure reference" className={controlClass} onChange={event => setForm(current => ({ ...current, gasMaximumAllowedReference: event.target.value as DutyForm['gasMaximumAllowedReference'] }))} value={form.gasMaximumAllowedReference}><option value="gauge">Gauge · kPa(g)</option><option value="absolute">Absolute · kPa(a)</option></select></Field>
                </div>
                <Field label="Documented site minimum · kPa"><input aria-label="Project gas site supply minimum pressure (kPa)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSiteSupplyMinimumKPa: event.target.value }))} type="number" value={form.gasSiteSupplyMinimumKPa} /></Field>
                <Field label="Site reference"><select aria-label="Project gas site supply pressure reference" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSiteSupplyMinimumReference: event.target.value as DutyForm['gasSiteSupplyMinimumReference'] }))} value={form.gasSiteSupplyMinimumReference}><option value="gauge">Gauge · kPa(g)</option><option value="absolute">Absolute · kPa(a)</option></select></Field>
                <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Documented site maximum · kPa" hint="Transporter maximum operating pressure. Without it the upper bound is measured from the site minimum and cannot establish an over-pressure condition."><input aria-label="Project gas site supply maximum pressure (kPa)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSiteSupplyMaximumKPa: event.target.value }))} type="number" value={form.gasSiteSupplyMaximumKPa} /></Field>
                  <Field label="Site maximum reference"><select aria-label="Project gas site supply maximum pressure reference" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSiteSupplyMaximumReference: event.target.value as DutyForm['gasSiteSupplyMaximumReference'] }))} value={form.gasSiteSupplyMaximumReference}><option value="gauge">Gauge · kPa(g)</option><option value="absolute">Absolute · kPa(a)</option></select></Field>
                </div>

                <div className="border-t border-border pt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted">Interconnect delivery capability</p>
                  <div className="mt-3 space-y-3">
                    <Field label="Quantity basis" hint="Only a firm transport agreement sets the scenario's firm gas capacity; an indicative rating is recorded and derated but never promoted.">
                      <select aria-label="Interconnect quantity basis" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSupplyQuantityBasis: event.target.value as DutyForm['gasSupplyQuantityBasis'] }))} value={form.gasSupplyQuantityBasis}>
                        <option value="">Not documented</option>
                        <option value="firm-transport-agreement">Firm transport agreement</option>
                        <option value="indicative">Indicative · scoping / ROM class</option>
                      </select>
                    </Field>
                    {form.gasSupplyQuantityBasis !== '' && <>
                      <Field label="Capacity units">
                        <select aria-label="Interconnect capacity units" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSupplyCapacityKind: event.target.value as DutyForm['gasSupplyCapacityKind'] }))} value={form.gasSupplyCapacityKind}>
                          <option value="standard-volume">Volumetric · MMscf/day</option>
                          <option value="energy">Energy · Dth/day</option>
                        </select>
                      </Field>
                      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label={form.gasSupplyCapacityKind === 'energy' ? 'Station design · Dth/day' : 'Station design · MMscf/day'} hint="Meter/station rating at the pressure below."><input aria-label="Interconnect station design capacity" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyStationCapacity: event.target.value }))} type="number" value={form.gasSupplyStationCapacity} /></Field>
                        <Field label={form.gasSupplyCapacityKind === 'energy' ? 'Contract MDQ · Dth/day' : 'Contract MDQ · MMscf/day'} hint="Optional. Physics derates; contracts do not."><input aria-label="Interconnect contract maximum daily quantity" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyContractMaxDailyQuantity: event.target.value }))} type="number" value={form.gasSupplyContractMaxDailyQuantity} /></Field>
                      </div>
                      {form.gasSupplyCapacityKind === 'standard-volume' && <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
                        <Field label="HHV · Btu/scf" hint="From the tariff gas-quality sheet. Required — a volumetric capacity cannot be converted without it."><input aria-label="Interconnect documented heating value" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyHhvBtuPerScf: event.target.value }))} type="number" value={form.gasSupplyHhvBtuPerScf} /></Field>
                        <Field label="Base pressure · kPa(a)" hint="14.65 / 14.696 / 14.73 psia are all in use — a 0.5% spread."><input aria-label="Interconnect standard base pressure" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyBasePressureKPaAbsolute: event.target.value }))} type="number" value={form.gasSupplyBasePressureKPaAbsolute} /></Field>
                        <Field label="Base temperature · °C"><input aria-label="Interconnect standard base temperature" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSupplyBaseTemperatureC: event.target.value }))} type="number" value={form.gasSupplyBaseTemperatureC} /></Field>
                      </div>}
                      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Rated at pressure · kPa" hint="The pressure the station capacity was rated at."><input aria-label="Interconnect rated pressure" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyRatedAtPressureKPa: event.target.value }))} type="number" value={form.gasSupplyRatedAtPressureKPa} /></Field>
                        <Field label="Rated reference"><select aria-label="Interconnect rated pressure reference" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSupplyRatedAtPressureReference: event.target.value as DutyForm['gasSupplyRatedAtPressureReference'] }))} value={form.gasSupplyRatedAtPressureReference}><option value="gauge">Gauge · kPa(g)</option><option value="absolute">Absolute · kPa(a)</option></select></Field>
                      </div>
                      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Z at rated pressure" hint="Optional, both or neither. Without it the derate runs ideal-gas and carries a 3% band."><input aria-label="Interconnect compressibility at rated pressure" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyZAtRated: event.target.value }))} step="0.001" type="number" value={form.gasSupplyZAtRated} /></Field>
                        <Field label="Z at minimum pressure"><input aria-label="Interconnect compressibility at minimum delivery pressure" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, gasSupplyZAtMinimum: event.target.value }))} step="0.001" type="number" value={form.gasSupplyZAtMinimum} /></Field>
                      </div>
                      <Field label="Operating case" hint="Flowing condition the rating applies to."><input aria-label="Interconnect operating case" className={controlClass} onChange={event => setForm(current => ({ ...current, gasSupplyOperatingCase: event.target.value }))} value={form.gasSupplyOperatingCase} /></Field>
                    </>}
                  </div>
                </div>
              </div>
            </fieldset>
            </OptionalGroup>

            <OptionalGroup hint="Project water duty — intensity or absolute design day." status={draftEvidenceCompleteness.statuses.waterBalance} testId="project-duty-group-water" title="Generation water">
            <fieldset disabled={editorDisabled}>
              <legend className="text-[10px] font-bold uppercase tracking-wider">Generation water</legend>
              <div className="mt-3 space-y-3">
                <Field label="Water source IDs" hint="Comma-separated evidence source IDs."><input aria-label="Project water source IDs" className={controlClass} onChange={event => setForm(current => ({ ...current, waterSourceIds: event.target.value }))} value={form.waterSourceIds} /></Field>
                <Field label="Duty basis"><select aria-label="Project generation water duty basis" className={controlClass} onChange={event => setForm(current => ({ ...current, waterMode: event.target.value as WaterMode }))} value={form.waterMode}><option value="none">Not supplied</option><option value="gross-output-intensity">Gross-output intensity</option><option value="absolute-design-day">Absolute design day</option></select></Field>
                {form.waterMode !== 'none' && <>
                  <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label={form.waterMode === 'absolute-design-day' ? 'Withdrawal · gal/day' : 'Withdrawal · gal/MWh'}><input aria-label="Project generation water withdrawal" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, waterWithdrawal: event.target.value }))} type="number" value={form.waterWithdrawal} /></Field>
                    <Field label={form.waterMode === 'absolute-design-day' ? 'Consumption · gal/day' : 'Consumption · gal/MWh'}><input aria-label="Project generation water consumption" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, waterConsumption: event.target.value }))} type="number" value={form.waterConsumption} /></Field>
                  </div>
                  {form.waterMode === 'absolute-design-day' && <Field label="Reference gross · MWh/day"><input aria-label="Project water reference gross generation (MWh per day)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, waterReferenceGrossMWhPerDay: event.target.value }))} type="number" value={form.waterReferenceGrossMWhPerDay} /></Field>}
                  <Field label="Operating case"><input aria-label="Project water operating case" className={controlClass} onChange={event => setForm(current => ({ ...current, waterOperatingCase: event.target.value }))} value={form.waterOperatingCase} /></Field>
                </>}
              </div>
            </fieldset>
            </OptionalGroup>
          </div>

          <OptionalGroup hint="Guaranteed vendor source level for the receptor screen." status={draftEvidenceCompleteness.statuses.acousticSource} testId="project-duty-group-acoustics" title="Broadband acoustic source">
          <fieldset className="border-t border-border pt-4" disabled={editorDisabled}>
            <legend className="text-[10px] font-bold uppercase tracking-wider">Broadband acoustic source</legend>
            {(input.projectDutyBasis?.acoustics?.acousticSources.length ?? 0) > 1 && <p className="mt-2 border-l-2 border-warning pl-2 text-[9px] text-text-muted">The first acoustic row is editable here; {input.projectDutyBasis!.acoustics!.acousticSources.length - 1} additional project row(s) are preserved unchanged on re-apply.</p>}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Acoustic source IDs" hint="Comma-separated evidence source IDs."><input aria-label="Project acoustic evidence source IDs" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticSourceIds: event.target.value }))} value={form.acousticSourceIds} /></Field>
              <Field label="Acoustic datum"><select aria-label="Project acoustic datum kind" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticMode: event.target.value as AcousticMode }))} value={form.acousticMode}><option value="none">Not supplied</option><option value="sound-power">Sound power · dBA re 1 pW</option><option value="reference-spl">Reference SPL · dBA at distance</option></select></Field>
              {form.acousticMode !== 'none' && <>
                <Field label="Source label"><input aria-label="Project acoustic source label" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticLabel: event.target.value }))} value={form.acousticLabel} /></Field>
                <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Quantity"><input aria-label="Project acoustic source quantity" className={controlClass} min="1" onChange={event => setForm(current => ({ ...current, acousticQuantity: event.target.value }))} type="number" value={form.acousticQuantity} /></Field>
                  <Field label="Quantity scope"><select aria-label="Project acoustic quantity scope" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticQuantityScope: event.target.value as DutyForm['acousticQuantityScope'] }))} value={form.acousticQuantityScope}><option value="per-generation-block">Per generation block</option><option value="site-total">Site total</option></select></Field>
                </div>
                <Field label="Operating case"><input aria-label="Project acoustic operating case" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticOperatingCase: event.target.value }))} value={form.acousticOperatingCase} /></Field>
                <Field label="A-weighted level · dBA"><input aria-label="Project acoustic level (dBA)" className={controlClass} max="250" min="-50" onChange={event => setForm(current => ({ ...current, acousticLevelDbA: event.target.value }))} type="number" value={form.acousticLevelDbA} /></Field>
                {form.acousticMode === 'sound-power' ? <Field label="Directivity correction · dB"><input aria-label="Project acoustic directivity correction (dB)" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticDirectivityCorrectionDb: event.target.value }))} type="number" value={form.acousticDirectivityCorrectionDb} /></Field> : <>
                  <Field label="Reference distance · m"><input aria-label="Project acoustic reference distance (m)" className={controlClass} min="0" onChange={event => setForm(current => ({ ...current, acousticReferenceDistanceM: event.target.value }))} type="number" value={form.acousticReferenceDistanceM} /></Field>
                  <Field label="Reference scope"><select aria-label="Project acoustic reference scope" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticReferenceScope: event.target.value as DutyForm['acousticReferenceScope'] }))} value={form.acousticReferenceScope}><option value="aggregate">Aggregate source</option><option value="per-unit">Per unit</option></select></Field>
                  <Field label="Basis label"><input aria-label="Project acoustic basis label" className={controlClass} onChange={event => setForm(current => ({ ...current, acousticBasisLabel: event.target.value }))} value={form.acousticBasisLabel} /></Field>
                </>}
              </>}
            </div>
          </fieldset>
          </OptionalGroup>
        </div>
      </div>

      {error && (
        <div aria-live="assertive" className="flex items-start gap-2 border-t border-danger/40 bg-danger/10 px-4 py-3 text-[10px] text-danger" data-testid="project-duty-error" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}
      {appliedStatus && <p aria-atomic="true" aria-live="polite" className="sr-only" data-testid="project-duty-apply-status" role="status">{appliedStatus}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
        <p className="max-w-3xl text-[9px] text-text-muted">
          Project data is hash-bound to the selected catalog duty. Gauge and absolute pressure references are never compared without a documented conversion. Republish the parcel overlay after changing installed reservations.
        </p>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          {draftDirty && bound && (
            <button className="inline-flex items-center gap-1 border border-warning/60 px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-warning hover:bg-warning/10 disabled:opacity-50 rounded-md" data-testid="project-duty-discard-edits" disabled={disabled} onClick={discardDraftEdits} type="button"><Undo2 className="h-3.5 w-3.5" /> Discard edits</button>
          )}
          {active && (
            <button className="inline-flex items-center gap-1 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-danger hover:text-danger disabled:opacity-50 rounded-md" disabled={disabled} onClick={clear} type="button"><RotateCcw className="h-3.5 w-3.5" /> Restore catalog duty</button>
          )}
          <button className="inline-flex items-center gap-1 border border-primary bg-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary-foreground hover:opacity-90 disabled:border-border disabled:bg-background disabled:text-text-muted disabled:opacity-70 rounded-md" disabled={disabled || !bound} onClick={apply} type="button"><Check className="h-3.5 w-3.5" /> {active ? 'Re-apply project dossier' : 'Apply project dossier'}</button>
        </div>
      </div>
    </section>
  );
}
